import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { post, get } from "@utils/axios_api";
import { toast } from 'react-toastify';

const schema = yup.object().shape({
    file: yup.mixed().required("엑셀 파일을 선택해주세요."),
});

export function useDepartmentFormModal({ onSuccess, onClose, compCode }) {
    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: { file: null },
        shouldFocusError: false,
    });

    const { handleSubmit, setValue, watch } = methods;
    const [previewData, setPreviewData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    // 샘플 다운로드 기능
    const handleSampleDownload = async () => {
        try {
            const response = await get("/admin/dept/downloadSampleExcel", {
                responseType: "blob",
            });
            const blob = new Blob([response], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = "부서등록_샘플양식.xlsx";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error("샘플 양식 다운로드 오류:", error);
        }
    };

    // 미리보기 API 호출
    const previewUpload = async (formData) => {
        try {
            setLoading(true);
            const response = await post("/admin/dept/uploadExcel", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (response.status !== false && response.result) {
                setPreviewData(response.result);
                setErrorMsg("");
            } else {
                setErrorMsg(response.message || "미리보기 데이터를 불러오는데 실패했습니다.");
            }
        } catch (error) {
            setErrorMsg("미리보기 처리 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    // 미리보기 후 등록 처리
    const saveData = async () => {
        if (previewData.length === 0) {
            setErrorMsg("먼저 미리보기 후 등록할 데이터를 확인해주세요.");
            toast.warn("먼저 미리보기 후 등록할 데이터를 확인해주세요.");
            return;
        }

        if (!window.confirm("등록하시겠습니까?")) return;

        try {
            setLoading(true);
            const payload = {
                compCode: compCode,
                previewData: previewData
            };
            const response = await post("/admin/dept/departmentAllSave", payload);

            if (response.status !== false) {
                const result = response.result || {};
                const successCount = result.successCount ?? 0;
                const duplicatedNames = result.duplicatedNames || [];
                let message = `${successCount}건 등록 완료.`;
                if (duplicatedNames.length > 0) {
                    message += ` 중복으로 제외된 항목: ${duplicatedNames.join(", ")}`;
                }
                toast.success(message);
                setPreviewData([]);
                methods.reset();
                if (onSuccess) onSuccess(response);
                if (onClose) onClose();
            } else {
                setErrorMsg(response.message || "등록에 실패하였습니다.");
                toast.error(response.message || "등록에 실패하였습니다.");
            }
        } catch (error) {
            setErrorMsg("등록 처리 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (data) => {
        const fileList = data.file;
        if (!fileList || fileList.length === 0) {
            toast.warn("업로드할 파일을 선택해주세요.");
            return;
        }

        if (!compCode) {
            toast.warn("회사를 선택해주세요.");
            return;
        }

        const file = fileList[0];
        const formData = new FormData();
        formData.append("file", file);
        formData.append("compCode", compCode);
        await previewUpload(formData);
    };

    return {
        methods,
        handleFormSubmit: handleSubmit(onSubmit),
        previewData,
        saveData,
        loading,
        errorMsg,
        setPreviewData,
        handleSampleDownload,
        setValue,
        watch,
    };
}